import { useFormSelector } from '@elfsight/widget-form-builder/widget';
import { useMutation } from '@tanstack/react-query';
import { useSettings } from '@wu/widget-core';
import { isCalendarIntegrationEnabled } from '../../common/is-calendar-integration-enabled';
import { APPOINTMENT_BOOKING_API_BASE_URL } from '../../constants/constants';
import { useCurrentTimeZone } from '../current-timezone-provider';
import { useDatetime } from '../datetime/datetime-provider';
import { formatTimeWithTimeZone } from '../datetime/utils/format-time-with-time-zone';
import { useServices } from '../service/services-provider';
import { getSessionId } from '../utils/get-session-id';
import { useCreateCalendarEventDescription } from './use-get-field-values-str';

export function useCreateCalendarEventMutation() {
  const {
    integrationGoogleCalendarAuth,
    integrationGoogleCalendarCalendar,
    widgetToken,
    widgetId,
    businessContactsAddress
  } = useSettings();
  const { timeZone } = useCurrentTimeZone();
  const { currentService } = useServices();
  const { fields } = useFormSelector();
  const { selectedTimeslot } = useDatetime();

  const connectionPid = integrationGoogleCalendarAuth?.identity;
  const calendarId = integrationGoogleCalendarCalendar?.id;
  const isGCEnabled = isCalendarIntegrationEnabled({
    integrationGoogleCalendarAuth,
    integrationGoogleCalendarCalendar
  });

  const description = useCreateCalendarEventDescription(Object.values(fields));

  return useMutation({
    mutationFn: async () => {
      if (!isGCEnabled || !selectedTimeslot || !currentService || !calendarId) {
        return;
      }

      const calendarIdEncoded = encodeURIComponent(calendarId);

      const params = new URLSearchParams({
        connection: connectionPid ?? '',
        calendarId: calendarIdEncoded,
        appointmentId: widgetId ?? ''
      });

      const formattedFrom = formatTimeWithTimeZone(
        selectedTimeslot.from,
        timeZone
      );
      const formattedTo = formatTimeWithTimeZone(selectedTimeslot.to, timeZone);
      const safeDescription =
        description.trim() || 'Appointment booking request';

      const response = await fetch(
        `${APPOINTMENT_BOOKING_API_BASE_URL}/appointment-booking/timeslot/reserve?${params}`,
        {
          method: 'POST',
          body: JSON.stringify({
            summary: currentService.name || 'No service name provided',
            location: businessContactsAddress || 'No location provided',
            description: safeDescription,
            from: formattedFrom,
            to: formattedTo,
            appointmentServiceId: currentService.appointmentServiceId
          }),
          headers: new Headers({
            'x-session-id': getSessionId(),
            'x-widget-token': widgetToken || ''
          })
        }
      );

      if (!response.ok) {
        throw new Error(
          response.status === 409
            ? 'The selected time slot is already taken'
            : 'Unexpected Error'
        );
      }

      const { payload } = (await response.json()) as {
        code: number;
        message?: string;
        payload?: { url: string };
      };

      return payload;
    },
    onError: (e) => {
      console.error(e);
      // @TODO print error for human
    },
    onSuccess: (data) => {
      console.log('Owner Google Calendar Event URL', data?.url);
    }
  });
}

// const formatShortISO = (date: Date) => formatISO(date).replace(/[-:]/g, '');
